import { Builder } from "@olga-food/schemas/lib/classes/schemas/catalog/Builder";
import { DeliveryType } from "@olga-food/schemas/lib/classes/schemas/order/delivery/Delivery";
import dayjs from "dayjs";

import { CatalogHelpers } from "./CatalogHelpers";
import { Currency } from "shared/utils/Currency";
import { OrderStorage } from "storage/OrderStorage";
import { OMath } from "shared/utils/OMath";

const GIFT_TYPE = {
  // ===== Valores adicioniais =====
  ADDITION: 1,

  // ===== Forma de cobrança =====
  PRICE: 2,
  PERCENT: 4,

  // ===== Cobrar com base em =====
  CART: 8,
  DELIVERY: 16,

  // ===== Tipo de uso do cupom =====
  FIRST_PURCHASE_EVER: 32,
  N_USE: 64,
  FREE_TO_USE: 128,
  NEXT_PURCHARSE: 256,

  CASHBACK_USE: 512,
  CASHBACK_EARN: 1024,
  PRODUCT: 2048,
};

const USE_TYPES = {
  ONLINE: 1,
  OFFLINE: 2,
  TAKEAWAY: 4,
  FASTFILA: 8,
};

export const DAYS = [
  {
    key: 0,
    full: "Domingo",
  },
  {
    key: 1,
    full: "Segunda-feira",
  },
  {
    key: 2,
    full: "Terça-feira",
  },
  {
    key: 3,
    full: "Quarta-feira",
  },
  {
    key: 4,
    full: "Quinta-feira",
  },
  {
    key: 5,
    full: "Sexta-feira",
  },
  {
    key: 6,
    full: "Sábado",
  },
];

const isType = (haystack, needle) => (needle & haystack) >= needle;

export class Benefit {
  static getTitle(coupon, programByPoint) {
    switch (coupon?.type) {
      case "PERCENTAGE_DISCOUNT":
      case "PRODUCT_DISCOUNT":
        return `Cupom de ${coupon.value || 0}%`;
      case "CASH_DISCOUNT":
      case "PRODUCT_CASH_DISCOUNT":
        return `Cupom de ${Currency.formatCurrency(coupon.value || 0)}`;
      case "DISCOUNT_ORDER":
        if (coupon.amount_type === "CASH")
          return `Cupom de ${Currency.formatCurrency(coupon.amount || 0)}`;

        if (coupon.amount_type === "PERCENT")
          return `Cupom de ${coupon.amount || 0}%`;
        break;
      case "CASHBACK_ACCUMULATE":
        if (coupon.cashback_cash_bonus)
          return `Cashback de ${Currency.formatCurrency(
            coupon.cashback_cash_bonus || 0,
          )}`;
        if (coupon.cashback_percent_bonus)
          return `Cashback de ${coupon.cashback_percent_bonus || 0}%`;
        break;
      case "CASHBACK":
        return programByPoint
          ? `${coupon.amount || coupon.value} pontos para cada R$100`
          : `Cashback de ${coupon.amount || coupon.value || 0}%`;
      case "FREE_DELIVERY":
        return `Cupom de Frete grátis`;
      case "CASHBACK_CONSUME":
        return `Usar saldo`;
      default:
        break;
    }

    return "Cupom não encontrado";
  }

  static getExpirationStatus(expirationDate) {
    if (!expirationDate) return;
    const validUntil = dayjs(expirationDate).format("DD/MM/YYYY");
    return `Válido até ${validUntil}`;

    // // Convert the expiration date to a dayjs object
    // const comparedDate = dayjs(expirationDate);

    // // Get the current date
    // const currentDate = dayjs();

    // // Calculate the difference in hours
    // const hourDifference = comparedDate.diff(currentDate, "hour");

    // // Get the remaining hours in the current day
    // const remainingHoursInDay = 24 - currentDate.hour();

    // // Compare the difference in hours with the given conditions
    // if (hourDifference < remainingHoursInDay) {
    //   return "Expira hoje";
    // } else if (hourDifference < 24) {
    //   return "Expira amanhã";
    // } else {
    //   // Calculate the difference in days
    //   const dayDifference = Math.ceil(comparedDate.diff(currentDate, "day", true));
    //   return `Expira em ${dayDifference} ${dayDifference === 1 ? 'dia' : 'dias'}`;
    // }
  }

  static getRules(coupon) {
    if (!coupon) return;

    const rules = [];

    if (coupon?.expiration?.days === null && coupon?.expiration?.end === null)
      rules.push("Nunca expira");

    if (coupon?.expiration?.end) {
      const endDate = dayjs(coupon.expiration?.end).format("DD/MM/YYYY");
      rules.push(`Válido até ${endDate}`);
    }

    if (coupon?.type == "FREE_DELIVERY") {
      rules.push("Aplicado somente ao valor da entrega");
    }

    if (!!coupon?.min_order) {
      rules.push(
        `Pedido mínimo ${Currency.formatCurrency(coupon?.min_order || 0)}`,
      );
    }

    if (["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon?.type)) {
      rules.push("Aplicado somente ao valor de produtos selecionados");
    }

    if (isType(coupon?.type_use, USE_TYPES.FASTFILA)) {
      rules.push(`Disponível para retirada pedindo no app (comer aqui)`);
    }

    if (isType(coupon?.type_use, USE_TYPES.TAKEAWAY)) {
      rules.push(`Disponível para retirada pedindo no app (levar)`);
    }

    if (isType(coupon?.type_use, USE_TYPES.ONLINE)) {
      rules.push(`Disponível para entrega`);
    }

    if (isType(coupon?.type_use, USE_TYPES.OFFLINE)) {
      rules.push(`Disponível para salão`);
    }

    return rules;
  }

  static getCode(coupon) {
    if (coupon?.code) {
      return `${coupon?.code}-${coupon?.pin}`;
    } else {
      return null;
    }
  }

  static isValidProductDiscount(coupon, cart) {
    const couponProducts = coupon?.product_list || [];
    const allowMultipleItems = coupon?.multiple_items;
    const cartCatalog = CatalogHelpers.clearUnselectedItems(cart?.catalog) || {
      items: [],
    };

    const props = ["items", "sizes"];
    let validSkuCount = {};

    const generateUniqueKey = (item) => {
      const randomSuffix = Math.floor(Math.random() * 100000)
        .toString()
        .padStart(5, "0");
      return `${item.id}-${randomSuffix}`;
    };

    const validateAndCountSKUs = (item) => {
      let localValidSkuCount = 0;
      for (const key of props) {
        if (item[key] && item[key].length > 0) {
          for (const subItemIndex in item[key]) {
            item[key][subItemIndex] = validateAndCountSKUs(
              item[key][subItemIndex],
            );
          }

          const validItems = item[key].filter(
            (i) => i && couponProducts.includes(i.sku),
          );

          localValidSkuCount += validItems.length;
          item[key] = validItems.length > 0 ? validItems : null;
        }
      }

      if (localValidSkuCount > 0) {
        const uniqueKey = generateUniqueKey(item);
        validSkuCount[uniqueKey] =
          (validSkuCount[uniqueKey] || 0) + localValidSkuCount;
      }
      return item;
    };

    Builder.executeOnSchema(cartCatalog, (item) => validateAndCountSKUs(item));

    let isValid;

    for (const [key, count] of Object.entries(validSkuCount)) {
      if (allowMultipleItems) isValid = count >= 1;
      else isValid = count === 1;
    }

    return isValid;
  }

  static getProductDiscountValue(coupon, cartItems = []) {
    if (!["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type)) {
      return 0;
    }

    const couponProducts = coupon?.product_list || [];
    const giftValue = coupon?.value || 0;

    let baseValue = 0;
    let productPrice = null;

    const couponType = Benefit.parseCouponType(coupon);

    for (let i = 0; i < cartItems.length; i++) {
      const product = JSON.parse(JSON.stringify(cartItems[i]));
      const productSchema = Builder.mountSchema(product);
      CatalogHelpers.executeOnSchema(productSchema, (item, parent) => {
        if (item.sku && couponProducts.includes(item.sku)) {
          if (
            item.type === "size" &&
            parent &&
            parent.quantity &&
            parent.items?.length
          ) {
            if (item.selected && item.quantity) {
              productPrice = productPrice + item.price * item.quantity;
            }
          } else if (item.price) {
            if (item.selected && item.quantity) {
              productPrice = productPrice + item.price * item.quantity;
            }
          }
          /** Remoção desse bloco foi porque ele estava zerando o valor de um item que já tinha sido aplicado o desconto */
          // else {
          // 	productPrice = null;
          // }
        }
      });

      if (productPrice !== null && !Number.isNaN(productPrice)) {
        baseValue = productPrice;
        break;
      }
    }

    if (isType(couponType, GIFT_TYPE.PRICE)) {
      return +Math.min(baseValue, giftValue).toFixed(2);
    }

    return +OMath.div(
      OMath.times(baseValue, Math.min(giftValue, 100)),
      100,
    ).toFixed(2);
  }

  static canBeUsed(coupon, isOffline = false) {
    if (coupon?.type) {
      const { cart, delivery } = OrderStorage.getOrder();

      if (coupon.enabled === false) return false;

      if (
        coupon.visibility?.length > 0 &&
        !Benefit.isCurrentTimeValid(coupon.visibility)
      )
        return false;

      if (isOffline) return true;

      if (coupon.units_valid && coupon.units_valid.length > 0) return false;

      if (coupon.type == "CASHBACK_CONSUME") return true;

      if (
        coupon.type === "FREE_DELIVERY" &&
        delivery?.type !== DeliveryType.DELIVER_ON_ADDRESS
      )
        return;

      if (cart.total < coupon.min_order) return false;

      if (["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type)) {
        return Benefit.isValidProductDiscount(coupon, cart);
      }

      const isFastFilaOk =
        (isType(coupon.type_use, USE_TYPES.FASTFILA) ||
          isType(coupon.type_use, USE_TYPES.OFFLINE)) &&
        delivery?.type == DeliveryType.INDOOR;
      const isTakeAwayOk =
        isType(coupon.type_use, USE_TYPES.TAKEAWAY) &&
        delivery?.type == DeliveryType.GET_ON_PLACE;
      const isDeliveryOk =
        isType(coupon.type_use, USE_TYPES.ONLINE) &&
        delivery?.type == DeliveryType.DELIVER_ON_ADDRESS;

      if (!(isFastFilaOk || isTakeAwayOk || isDeliveryOk)) return false;

      return true;
    }

    return false;
  }

  static getPlacesCanBeUsed(coupon) {
    if (!coupon.type_use) return null;

    let places = "Disponível para";

    const types = [];

    if (isType(coupon.type_use, USE_TYPES.TAKEAWAY)) {
      types.push(`retirada`);
    }

    if (isType(coupon.type_use, USE_TYPES.ONLINE)) {
      types.push(`delivery`);
    }

    if (isType(coupon.type_use, USE_TYPES.OFFLINE)) {
      types.push(`loja`);
    }

    if (types.length === 1) {
      places = places.concat(` ${types[0]}`);
    } else if (types.length > 1) {
      const lastType = types.pop();
      places = places.concat(` ${types.join(", ")} e ${lastType}`);
    }

    return places;
  }

  static isCurrentTimeValid(visibility) {
    const currentDayOfWeek = dayjs().day();

    const daySchedule = visibility.find(
      (item) => item.weekDay === currentDayOfWeek,
    );

    if (!daySchedule) return false;

    return daySchedule.times.some((timeRange) => {
      const start = Number(timeRange.start);
      const end = Number(timeRange.end);
      const currentTime = Number(dayjs().format("HHmm"));

      return currentTime >= start && currentTime <= end;
    });
  }

  static notAvailableText(coupon) {
    const { cart, delivery } = OrderStorage.getOrder();
    const isFastFilaOk =
      isType(coupon.type_use, USE_TYPES.FASTFILA) &&
      delivery?.type == DeliveryType.INDOOR;
    const isTakeAwayOk =
      isType(coupon.type_use, USE_TYPES.TAKEAWAY) &&
      delivery?.type == DeliveryType.GET_ON_PLACE;
    const isDeliveryOk =
      isType(coupon.type_use, USE_TYPES.ONLINE) &&
      delivery?.type == DeliveryType.DELIVER_ON_ADDRESS;

    if (!coupon.enabled) {
      return "Indisponível no momento, verifique as regras.";
    }

    if (
      coupon.type_use !== USE_TYPES.OFFLINE &&
      cart.total < coupon.min_order
    ) {
      return "Indisponível no momento, verifique as regras.";
    }

    if (
      coupon.type === "FREE_DELIVERY" &&
      delivery?.type !== DeliveryType.DELIVER_ON_ADDRESS
    ) {
      return "Indisponível no momento, verifique as regras.";
    }

    if (
      ["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type) &&
      !Benefit.isValidProductDiscount(coupon, cart)
    ) {
      return "Indisponível no momento, verifique as regras.";
    }

    if (!(isFastFilaOk || isTakeAwayOk || isDeliveryOk)) {
      return "Indisponível no momento, verifique as regras.";
    }
  }

  static parseCouponType(coupon) {
    switch (coupon.type) {
      case "CASH_DISCOUNT":
        return GIFT_TYPE.PRICE | GIFT_TYPE.CART | GIFT_TYPE.FREE_TO_USE;
      case "PERCENTAGE_DISCOUNT":
        return GIFT_TYPE.PERCENT | GIFT_TYPE.CART | GIFT_TYPE.FREE_TO_USE;
      case "PRODUCT_DISCOUNT":
        return GIFT_TYPE.PERCENT | GIFT_TYPE.PRODUCT | GIFT_TYPE.FREE_TO_USE;
      case "PRODUCT_CASH_DISCOUNT":
        return GIFT_TYPE.PRICE | GIFT_TYPE.PRODUCT | GIFT_TYPE.FREE_TO_USE;
      case "CASHBACK_POINTS":
        return (
          GIFT_TYPE.CASHBACK_POINTS_EARN |
          GIFT_TYPE.PERCENT |
          GIFT_TYPE.CART |
          GIFT_TYPE.FREE_TO_USE
        );
      case "CASHBACK":
        return (
          GIFT_TYPE.CASHBACK_EARN |
          GIFT_TYPE.PERCENT |
          GIFT_TYPE.CART |
          GIFT_TYPE.FREE_TO_USE
        );
      case "CASHBACK_CONSUME":
        return (
          GIFT_TYPE.CASHBACK_USE |
          GIFT_TYPE.PRICE |
          GIFT_TYPE.CART |
          GIFT_TYPE.FREE_TO_USE
        );
      case "FREE_DELIVERY":
        return GIFT_TYPE.PRICE | GIFT_TYPE.DELIVERY | GIFT_TYPE.FREE_TO_USE;
      default:
        break;
    }

    return null;
  }

  static getCartProductsBySku(couponSkus, cartItems) {
    if (!couponSkus?.length || !cartItems?.length) {
      return [];
    }

    const discount_products = [];

    const cartCatalog = { items: cartItems };
    const discountAvailableProductSkus = CatalogHelpers.getProductsBySkus(
      cartCatalog,
      couponSkus,
    );

    if (discountAvailableProductSkus[0]) {
      discount_products.push(discountAvailableProductSkus[0]);
    }
    return discount_products;
  }

  static parseCouponToOrderGift(coupon, cartItems = []) {
    const gift = {
      id: coupon.coupon || coupon.id,
      code: Benefit.getCode(coupon),
      type: Benefit.parseCouponType(coupon),
      value: coupon.value || 0,
      ...(coupon.type == "CASHBACK_CONSUME" ? { amount: coupon.amount } : {}),
      min_order: coupon.min_order || coupon.min_order_amount,
      expiration: coupon.expiration?.end,
      ...(["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type)
        ? {
            target_id: CatalogHelpers.getFirstItemSkuOnSkuList(
              cartItems,
              coupon.product_list,
            ),
          }
        : {}),
      created_at: coupon.created_at,
    };

    const giftSchema = gift;

    return giftSchema;
  }
}
